import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AppRoutes from 'constants/AppRoutes';
import { DATA_SET_PAGINATION_COUNT } from 'constants/dataSets';
import {
  KEY_PERMISSIONS,
  MATERIALITY_ASSESSMENT_STATUS,
  MODAL_REFINEMENT_QUESTIONS,
  TASK_MANAGEMENT_PERMISSIONS,
} from 'constants/general';
import { SECTION_TYPE, STATUS_SECTIONS } from 'constants/materialAssessment';
import useErrorMessage from 'hooks/useErrorMessage';
import usePillarModulePermission from 'hooks/usePillarModulePermission';
import useUpdateStatusSection from 'hooks/useUpdateStatusSection';
import i18n from 'i18n';
import { useAppSelector } from 'store/hooks/useApp';
import { selectSelectedVC } from 'store/slices/general/selectors';
import { setSelectedVC } from 'store/slices/general/slice';
import {
  useGetChartDataQuery,
  useGetSingleMAInitiateQuery,
  useGetStateTopicRefinement2Query,
  useGetStateTopicRefinement3Query,
  useGetStateTopicRefinementQuery,
  useGetTopicRefinementListQuery,
  useLazyGetRefinementDisclosuresRegionsQuery,
  useLazyGetRefinementQuestionsCompaniesQuery,
  useUpdateToggleTopicRefinementMutation,
  useUpdateTopicRefinement2Mutation,
  useUpdateTopicRefinement3Mutation,
  useUpdateTopicRefinementMutation,
} from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import { useUserMeQuery } from 'store/slices/user/apis/user';

import MATopicRefinementView from './MATopicRefinementView';

import type IChartItem from 'store/slices/materialAssessment/interfaces/IChartItem';
import type ITopicRefinementItem from 'store/slices/materialAssessment/interfaces/ITopicRefinementItem';
import type MATopicRefinementProps from './interfaces/MATopicRefinementProps';

function MATopicRefinement({ topicRefinementII, IROIdentification }:MATopicRefinementProps) {
  const navigate = useNavigate();
  const { recordId = '' } = useParams();
  const dispatch = useDispatch();
  const [itemsTR, setItemsTR] = useState<ITopicRefinementItem[]>([]);
  const [offset, setOffset] = useState(0);
  const [observe, setObserve] = useState(false);
  const [observe2, setObserve2] = useState(false);
  const [observe3, setObserve3] = useState(false);
  const [chartsData, setChartsData] = useState<IChartItem[]>([]);

  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [openModalRefused, setOpenModalRefused] = useState(false);
  const [currentId, setCurrentId] = useState<number | null>(null);

  const [currentGroupId, setCurrentGroupId] = useState<number | null>(null);
  const [currentType, setCurrentType] = useState<string | null>(null);
  const [currentGroupType, setCurrentGroupType] = useState<string[] | null>(null);

  const selectVC = useAppSelector(selectSelectedVC);
  const { data: userData } = useUserMeQuery();
  const { access } = usePillarModulePermission({
    pillarModuleKey: KEY_PERMISSIONS.MATERIALITY_ASSESSMENT_MANAGEMENT,
    userId: userData?.data?.id,
  });
  const { data: singleMA, isFetching: isLoadingSingleMA } = useGetSingleMAInitiateQuery({ id: recordId });
  const isViewOnly = access === TASK_MANAGEMENT_PERMISSIONS.VIEW_ONLY
  || access === TASK_MANAGEMENT_PERMISSIONS.SIGN_OFF
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.APPROVED
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.PENDING_REVIEW;

  const {
    data: stateTopicRefinementData,
    isFetching: isFetchingState,
    refetch: refetchState,
  } = useGetStateTopicRefinementQuery({ maId: recordId });
  const {
    data: stateTopicRefinement2Data,
    isFetching: isFetchingState2,
    refetch: refetchState2,
  } = useGetStateTopicRefinement2Query({ maId: recordId });
  const {
    data: stateTopicRefinement3Data,
    isFetching: isFetchingState3,
    refetch: refetchState3,
  } = useGetStateTopicRefinement3Query({ maId: recordId });
  const {
    data: chartItems,
    isFetching: isFetchingChart,
    refetch: refetchChart,
  } = useGetChartDataQuery({
    maId: recordId,
    topicId: selectedItemId || undefined,
    valueChainMapId: selectVC ? Number(selectVC) : undefined,
  });

  const [getRefinementQuestionsCompanies] = useLazyGetRefinementQuestionsCompaniesQuery();
  const [getRefinementDisclosuresRegions] = useLazyGetRefinementDisclosuresRegionsQuery();

  useEffect(() => {
    if (currentType === MODAL_REFINEMENT_QUESTIONS.COMPANIES && currentGroupId) {
      getRefinementQuestionsCompanies({
        maId: recordId,
        id: currentGroupId,
      }).then((response) => {
        setCurrentGroupType(response?.data?.data?.items || null);
      });
    }

    if (currentType === MODAL_REFINEMENT_QUESTIONS.REGIONS && currentGroupId) {
      getRefinementDisclosuresRegions({
        maId: recordId,
        id: currentGroupId,
      }).then((response) => {
        setCurrentGroupType(response?.data?.data?.items || null);
      });
    }
  }, [currentType, currentGroupId, recordId,
    getRefinementQuestionsCompanies, getRefinementDisclosuresRegions]);

  const handleGetTopicType = () => {
    if (topicRefinementII) {
      return SECTION_TYPE.TOPIC_REFINEMENT_2;
    }
    if (IROIdentification) {
      return SECTION_TYPE.IRO_REFINEMENT;
    }
    return SECTION_TYPE.TOPIC_REFINEMENT_1;
  };

  const { updateStatus } = useUpdateStatusSection({
    recordId,
    type: handleGetTopicType(),
    active: true,
    status: itemsTR.length > 0 ? STATUS_SECTIONS.COMPLETED : STATUS_SECTIONS.IN_PROGRESS,
  });
  const [updateTopicRefinement] = useUpdateTopicRefinementMutation();
  const [updateTopicRefinement2] = useUpdateTopicRefinement2Mutation();
  const [updateTopicRefinement3] = useUpdateTopicRefinement3Mutation();
  const [updateToggleTopicRefinement] = useUpdateToggleTopicRefinementMutation();

  const {
    data: topicRefinementListData,
    isFetching: isFetchingTopicRefinementList,
    error: fetchError,
    refetch: refetchTopicRefinementList,
  } = useGetTopicRefinementListQuery({
    maId: recordId,
    valueChainMapIds: selectVC ? [selectVC] : [],
    offset,
  }, {
    refetchOnMountOrArgChange: true,
  });

  const errorMessage = useErrorMessage(fetchError);

  useEffect(() => {
    dispatch(setSelectedVC(''));
  }, [recordId, dispatch]);

  useEffect(() => {
    if (errorMessage && errorMessage !== '') {
      toast.error(i18n.t('notifications.errorHandlerTextMessage', { message: errorMessage }));
    }
  }, [errorMessage]);

  const { meta } = topicRefinementListData?.data || {};

  const hasMore = meta?.hasNext || false;

  const onLoadMore = () => {
    if (hasMore) {
      setOffset((prev) => prev + DATA_SET_PAGINATION_COUNT);
    }
  };

  useEffect(() => {
    if (topicRefinementListData && topicRefinementListData?.data?.items) {
      const newDataItems = topicRefinementListData.data.items;
      setItemsTR((prev) => {
        if (offset === 0) {
          return newDataItems.map((item) => ({ ...item, custom: true }));
        }
        const existingIds = new Set(prev.map((item) => item.id));
        const newItems = newDataItems
          .filter((item) => !existingIds.has(item.id))
          .map((item) => ({ ...item, custom: true }));
        if (newItems.length === 0) return prev;
        return [...prev, ...newItems];
      });
    }
  }, [topicRefinementListData, offset]);

  useEffect(() => {
    if (chartItems && chartItems?.data.length > 0) {
      setChartsData(chartItems.data);
    } else {
      setChartsData([]);
    }
  }, [chartItems]);

  const handleCancel = () => {
    navigate(AppRoutes.materialityAssessment);
  };

  const handleNext = () => {
    updateStatus();
    if (!topicRefinementII && !IROIdentification) {
      navigate(AppRoutes.materialityAssessmentSurveyCreation.replace(':recordId', recordId));
    } else if (!topicRefinementII && IROIdentification) {
      navigate(AppRoutes.materialityAssessmentIROScoring.replace(':recordId', recordId));
    } else {
      navigate(AppRoutes.materialityAssessmentIROIdentification.replace(':recordId', recordId));
    }
  };

  const onPrevious = () => {
    if (!topicRefinementII && !IROIdentification) {
      navigate(AppRoutes.materialityAssessmentTopicIdentification.replace(':recordId', recordId));
    } else if (!topicRefinementII && IROIdentification) {
      navigate(AppRoutes.materialityAssessmentIROIdentification.replace(':recordId', recordId));
    } else {
      navigate(AppRoutes.materialityAssessmentInsightsDashboard.replace(':recordId', recordId));
    }
  };

  const handleUpdateData = useCallback(() => {
    setOffset(0);
    if (topicRefinementII) {
      updateTopicRefinement2({ maId: recordId }).then(() => {
        toast.success(i18n.t('notifications.updateTopicRefinement'));
        setTimeout(() => {
          refetchState2();
          setOffset(0);
          refetchTopicRefinementList();
          setObserve2(true);
        }, 1000);
      }).catch(() => {
        toast.error(i18n.t('notifications.errorUpdateTopicRefinement'));
      });
    } else if (IROIdentification) {
      updateTopicRefinement3({ maId: recordId }).then(() => {
        toast.success(i18n.t('notifications.updateTopicRefinement'));
        setTimeout(() => {
          refetchState3();
          setOffset(0);
          refetchTopicRefinementList();
          setObserve3(true);
        }, 1000);
      }).catch(() => {
        toast.error(i18n.t('notifications.errorUpdateTopicRefinement'));
      });
    } else {
      updateTopicRefinement({ maId: recordId }).then(() => {
        toast.success(i18n.t('notifications.updateTopicRefinement'));
        setTimeout(() => {
          refetchState();
          setOffset(0);
          refetchTopicRefinementList();
          setObserve(true);
        }, 1000);
      }).catch(() => {
        toast.error(i18n.t('notifications.errorUpdateTopicRefinement'));
      });
    }
    refetchChart();
  }, [topicRefinementII, recordId, updateTopicRefinement2,
    updateTopicRefinement, refetchState, refetchState2, refetchTopicRefinementList,
    IROIdentification, updateTopicRefinement3, refetchState3, refetchChart]);

  useEffect(() => {
    if (observe && !stateTopicRefinementData?.data?.upToDate) {
      setTimeout(() => {
        refetchState();
      }, 5000);
    } else {
      setObserve(false);
    }

    if (observe2 && !stateTopicRefinement2Data?.data?.upToDate) {
      setTimeout(() => {
        refetchState2();
      }, 5000);
    } else {
      setObserve(false);
    }

    if (observe3 && !stateTopicRefinement3Data?.data?.upToDate) {
      setTimeout(() => {
        refetchState3();
      }, 5000);
    } else {
      setObserve(false);
    }
  }, [observe, stateTopicRefinementData, stateTopicRefinement2Data,
    topicRefinementII, handleUpdateData, refetchState, refetchState2,
    stateTopicRefinement3Data, refetchState3, observe2, observe3]);

  const handleToggleTopicRefinement = (id: number) => {
    setCurrentId(id);
    setOpenModalRefused(true);
  };

  const handleBackToMainChart = () => {
    setSelectedItemId(null);
    setTimeout(() => {
      refetchChart();
    }, 200);
  };

  const handleSelectVC = (value: string) => {
    dispatch(setSelectedVC(value));
    setSelectedItemId(null);
  };

  const handleToItem = (id: number) => {
    navigate(
      AppRoutes.materialityAssessmentRespondentSurveys
        .replace(':recordId', recordId).replace(':surveyId', `${id}`).replace(':vcmId', `${selectVC}`),
    );
  };

  const handleDisableButtonUpdate = () => {
    if (topicRefinementII) {
      return !!stateTopicRefinement2Data?.data?.upToDate;
    }
    if (IROIdentification) {
      return !!stateTopicRefinement3Data?.data?.upToDate;
    }
    return !!stateTopicRefinementData?.data?.upToDate;
  };

  const handleUpdateTopicRefinement = (id: number, comment: string, isActive: boolean) => {
    updateToggleTopicRefinement({
      maId: recordId,
      id,
      data: {
        isActive: !isActive,
        reason: comment,
      },
    }).then((response) => {
      if ('data' in response && response.data?.data) {
        const { maTopicAnalysisLatestLog } = response.data.data;
        setItemsTR((prev) => prev.map((item) => {
          if (item.id === currentId) {
            return { ...item, isActive: !isActive, maTopicAnalysisLatestLog };
          }
          return item;
        }));
      }
      refetchChart();
      toast.success(i18n.t('notifications.updateTopicRefinement'));
      refetchChart();
    }).catch(() => {
      toast.error(i18n.t('notifications.errorUpdateTopicRefinement'));
    });
  };

  const onRejectModal = () => {
    setOpenModalRefused(false);
  };

  const onSubmitModal = (comment: string) => {
    setOpenModalRefused(false);
    if (currentId) {
      const isActive = itemsTR.find((item) => item.id === currentId)?.isActive;
      handleUpdateTopicRefinement(currentId, comment, isActive || false);
    }
  };

  const handleCloseModalRefinementQuestions = () => {
    setCurrentType(null);
    setCurrentGroupId(null);
    setCurrentGroupType(null);
  };

  const handleOpenModalRefinementQuestions = (id: number, type: string) => {
    setCurrentGroupId(id);
    setCurrentType(type);
  };

  const loading = isFetchingTopicRefinementList || isFetchingState || isFetchingState2 || isLoadingSingleMA
  || isFetchingState3;

  return (
    <MATopicRefinementView
      items={itemsTR}
      isEmptyList={itemsTR.length === 0}
      loading={loading}
      handleCancel={handleCancel}
      handleNext={handleNext}
      handlePrevious={onPrevious}
      onLoadMore={onLoadMore}
      hasMore={hasMore}
      handleUpdateData={handleUpdateData}
      disableButtonUpdate={handleDisableButtonUpdate()}
      handleToggleTopicRefinement={handleToggleTopicRefinement}
      topicRefinementII={topicRefinementII}
      chartItems={chartsData}
      isFetchingChart={isFetchingChart}
      selectedItemId={selectedItemId}
      setSelectedItemId={setSelectedItemId}
      handleBackToMainChart={handleBackToMainChart}
      handleToItem={IROIdentification ? handleToItem : undefined}
      IROIdentification={IROIdentification}
      vcOptions={singleMA?.data?.valueChainMaps?.map((itemVC) => ({ label: itemVC.name, value: `${itemVC.id}` })) || []}
      valueSelector={selectVC}
      setValueSelector={handleSelectVC}
      openModalRefused={openModalRefused}
      onRejectModal={onRejectModal}
      onSubmitModal={onSubmitModal}
      currentType={currentType}
      currentGroupId={currentGroupId}
      recordId={recordId}
      handleCloseModalRefinementQuestions={handleCloseModalRefinementQuestions}
      handleOpenModalRefinementQuestions={handleOpenModalRefinementQuestions}
      currentGroupType={currentGroupType}
      disableToggles={isViewOnly}
    />
  );
}

export default MATopicRefinement;
